import { VStack, Flex, Button, Heading, List, ListItem, UnorderedList, Spacer, Box } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertCard } from "../../components/AlertCard";
import { FileTree } from "../../components/FileTree";
import FileUploadArea from "../../components/FileUploadArea";
import { PageLayout } from "../../components/PageLayout";
import { TargetSelectStage } from "../../components/TargetSelectStage";
import { parseZip, parseMsprFiles, convertAndDownloadZip } from "../../utils/files";
import Mspr from "../../utils/Mspr";
import { MotorSynthPresets, Bank, Preset } from "../../utils/types";

const formatDatetime = (datetime: Date) => {
  // returns date in the format YYYY-MM-DD-HH-MM-SS
  return datetime.toISOString()
    .replaceAll(':', '-')
    .replaceAll('.', '-')
    .replaceAll('T', '-')
    .slice(0, -5);
}

export type ExportTarget = 'MKv1.2' | 'MK2';

const TARGET_TO_GCA_PRESET: Record<ExportTarget, string> = {
  'MKv1.2': 'GCA_PRESETS_LEGACY.zip',
  'MK2': 'GCA_PRESETS_MK2.zip',
}

export const PresetConverter = () => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>();
  const [presetTree, setPresetTree] = useState<MotorSynthPresets>(Mspr.createEmptyTree());
  const [presetContents, setPresetContents] = useState<Record<string, string>>({});
  const [skippedPresets, setSkippedPresets] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [target, setTarget] = useState<ExportTarget | undefined>(undefined);
  const [isGcaPresetsLoaded, setIsGcaPresetsLoaded] = useState(false);

  const onClear = () => {
    setSelectedFiles(undefined);
    setPresetTree(Mspr.createEmptyTree());
    setPresetContents({});
    setSkippedPresets([]);
    setIsGcaPresetsLoaded(false);
  };

  const handleBankDrop = (item: Bank, targetIndex: number) => {
    if (!presetTree) return;
    const { banks } = presetTree;
    const bank = item as Bank;
    const sourceIndex = banks.findIndex((b) => b.uniqueId === bank.uniqueId);
    if (sourceIndex === -1) return;
    // The dropzone is always before the target bank, except when the target bank is the last bank
    if (sourceIndex === targetIndex + 1) return;
    if (targetIndex === -1) {
      targetIndex += 1;
    }

    const [removed] = banks.splice(sourceIndex, 1);
    banks.splice(targetIndex, 0, removed);
    setPresetTree({ banks });
  }

  const handlePresetDrop = (item: Preset, targetBankId: string, targetIndex: number) => {
    if (!presetTree) return;
    const { banks } = presetTree;
    const preset = item as Preset;
    // find existing preset location
    const sourceBank = banks.find((b) => Mspr.containsPreset(b, preset));
    const presetIndex = sourceBank ? Mspr.findPreset(sourceBank, preset) : -1;
    // Offset by 1
    targetIndex = targetIndex + 1;
    if (!sourceBank || presetIndex === -1) return;
    // if the target bank is the same as the source bank, we need to adjust the index
    if (sourceBank.uniqueId === targetBankId && presetIndex < targetIndex) {
      targetIndex = targetIndex - 1;
    }
    const targetBank = banks.find((b) => b.uniqueId === targetBankId);
    if (!targetBank) return;
    const removed = Mspr.removePreset(sourceBank, preset);
    Mspr.insertPreset(targetBank, removed, targetIndex);
    setPresetTree({ banks });
  }

  const handleRename = (entity: Bank | Preset, newName: string) => {
    if (!presetTree) return;
    newName = newName.trim().toUpperCase();
    const { banks } = presetTree;
    const copy: Bank[] = structuredClone(banks);  // TODO: do not use structureClone for compatibility
    const isBank = entity.hasOwnProperty('presets');
    if (isBank) {
      const bank = entity as Bank;
      const bankIndex = copy.findIndex((b) => b.uniqueId === bank.uniqueId);
      copy[bankIndex].name = newName;
    } else {
      const preset = entity as Preset;
      const bankIndex = presetTree.banks.findIndex((b) => Mspr.containsPreset(b, preset));
      const presetIndex = Mspr.findPreset(presetTree.banks[bankIndex], preset);
      if (presetIndex === -1) return;
      const targetPreset = copy[bankIndex].presets[presetIndex];
      if (targetPreset) {
        targetPreset.name = newName;
      }
    }
    setPresetTree({ banks: copy });
  }

  /**
   * Will be added or merged, depending if any other presets are there.
   */
  const handleGcaPresetLoad = () => {
    if (!target) return;
    fetch(`${process.env.PUBLIC_URL}/${TARGET_TO_GCA_PRESET[target]}`)
      .then((response) => response.blob())
      .then((blob) => {
        const file = new File([blob], 'GCA_PRESETS.zip', {
          type: 'application/zip',
        });
        setSelectedFiles([file]);
      }
      );
  }

  const isZipFile = (file: File) => {
    const mimeTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'multipart/x-zip',
    ];
    return file.name.endsWith('.zip') && mimeTypes.includes(file.type);
  }

  const isMsprFile = (file: File) => {
    return file.name.endsWith('.mspr');
  }

  useEffect(() => {
    // read preset text async (await promises)
    if (selectedFiles && target) {
      setIsLoading(true);
      // group by file type (zip and mspr)
      const zipFiles = Array.from(selectedFiles).filter(isZipFile);
      const msprFiles = Array.from(selectedFiles).filter(isMsprFile);
      const totalSkipped: string[] = [];
      const updateState = (
        presetTree: MotorSynthPresets,
        presetContents: Record<string, string>,
        skipped: string[],
      ) => {
        setPresetTree(presetTree);
        setPresetContents(presetContents);
        totalSkipped.push(...skipped);
      }
      const zipPromises = zipFiles.map((f) => {
        return parseZip(f, target, presetContents, presetTree)
          .then(([presetTree, presetContents, skipped]) => updateState(presetTree, presetContents, skipped));
      });
      const msprPromise = parseMsprFiles(msprFiles, target, presetContents, presetTree)
        .then(([presetTree, presetContents, skipped]) => updateState(presetTree, presetContents, skipped));
      Promise.all([...zipPromises, msprPromise])
        .then(() => {
          if (zipFiles.map((f) => f.name).includes('GCA_PRESETS.zip')) {
            setIsGcaPresetsLoaded(true);
          }
        })
        .finally(() => {
          setSkippedPresets(totalSkipped);
          setIsLoading(false);
        });
    }
  }, [selectedFiles]);

  return (
    <PageLayout>
      <img src={process.env.PUBLIC_URL + '/ms-logo.svg'} style={{
        display: 'block',
        margin: '0 auto',
        maxWidth: '600px'
      }} />
      <TargetSelectStage
        onTargetSelect={setTarget}
        // transition='all 2s ease-in-out'
        // transform={target === undefined ? 'translateX(0)' : 'translateX(100%)'}
        display={target === undefined ? 'flex' : 'none'}
      />
      <VStack
        gap='1rem'
        display={target !== undefined ? 'flex' : 'none'}
        transition='all 2s ease-in-out'
      >
        <UnorderedList>
          {Object.values(t('converterInstructionLines', { returnObjects: true })).map((line, i) => (
            <ListItem key={i} color='white' fontSize='lg'>
              {line}
            </ListItem>
          ))}
          {target === 'MKv1.2' && <ListItem color='white' fontSize='lg'>
            {t('mk1SpecificInstruction')}
          </ListItem>}
        </UnorderedList>

        <FileUploadArea
          accept=".zip,.mspr"
          multiple={true}
          onChange={(files: FileList) => setSelectedFiles(Array.from(files))}
          title={t('dropAreaTitle')}
          isLoading={isLoading}
        />

        {skippedPresets && skippedPresets.length > 0 && (
          <AlertCard text={t('presetsSkippedError', {
            presetCount: `${skippedPresets.length} / ${skippedPresets.length + Object.keys(presetContents).length}`,
            target: `MOTOR SYNTH ${target}`,
          })} />)}
        <Flex
          gap='1rem'
          width='100%'
          flexDirection={{ base: 'column', md: 'row' }}
        >
          <Button
            variant='outline'
            onClick={handleGcaPresetLoad}
            isDisabled={isGcaPresetsLoaded || isLoading}
            visibility={isGcaPresetsLoaded ? 'hidden' : 'visible'}
          >
            {t(Object.keys(presetContents).length ? 'mergeGcaPresets' : 'loadGcaPresets')}
          </Button>
          <Spacer />
          <Button
            variant='outline'
            onClick={onClear}
            isDisabled={!Object.keys(presetContents).length}
          >
            {t('clear')}
          </Button>
          <Button
            colorScheme='primary'
            onClick={() => (presetTree && presetContents) &&
              convertAndDownloadZip(
                presetTree,
                presetContents,
                `PRESETS_${formatDatetime(new Date())}.zip`,
              )}
            isDisabled={!Object.keys(presetContents).length}
          >
            {t(target === 'MKv1.2' ? 'convertAndDownload' : 'download')}
          </Button>
        </Flex>

        {Object.keys(presetContents).length > 0 && <FileTree
          msPresets={presetTree}
          handleBankDrop={handleBankDrop}
          handlePresetDrop={handlePresetDrop}
          handleRename={handleRename}
        />}
      </VStack>
    </PageLayout>
  )
}